'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { Button } from '@components/ui/button';
import { Pause, Play } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { SLIDE_DURATION_MS, SLIDE_TRANSITION_MS } from './constants/slide-timing';
import { SlideHeading } from './slide-heading';
import { SlideProgress } from './slide-progress';
import { SlideVisual } from './slide-visual';
import type { AuthPanelDeck } from './types/auth-panel-deck';
const SLIDE_OFFSET_PX = 64;
const stageVariants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: SLIDE_OFFSET_PX * direction,
    filter: 'blur(8px)'
  }),
  center: {
    opacity: 1,
    x: 0,
    filter: 'blur(0px)'
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: -SLIDE_OFFSET_PX * direction,
    filter: 'blur(8px)'
  })
};
const fadeVariants = {
  enter: {
    opacity: 0
  },
  center: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};

/**
 * Plays a deck's slides in order, each sliding in from the
 * side it comes from. The pause button stops it until
 * pressed again; hovering or keyboard focus pause it while
 * they last.
 */
export function FeatureDeck(t0) {
  const $ = _c(59);
  const {
    deck
  } = t0;
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      index: 0,
      direction: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [t2, setPosition] = useState(t1);
  const {
    index,
    direction
  } = t2;
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  const count = deck.slides.length;
  const slide = deck.slides[index] ?? deck.slides[0];
  const autoplay = !reduceMotion && count > 1;
  const paused = isUserPaused || isHovered || hasKeyboardFocus;
  let t3;
  if ($[1] !== index) {
    t3 = next => {
      if (next === index) {
        return;
      }
      setPosition({
        index: next,
        direction: next > index ? 1 : -1
      });
    };
    $[1] = index;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const showSlide = t3;
  let t4;
  if ($[3] !== count) {
    t4 = () => setPosition(position => ({
      index: (position.index + 1) % count,
      direction: 1
    }));
    $[3] = count;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const advance = t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setIsHovered(true);
    t6 = () => setIsHovered(false);
    t7 = event => {
      if (event.target.matches(":focus-visible")) {
        setHasKeyboardFocus(true);
      }
    };
    t8 = event_0 => {
      if (!event_0.currentTarget.contains(event_0.relatedTarget)) {
        setHasKeyboardFocus(false);
      }
    };
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
  }
  const t9 = autoplay && !paused ? "off" : "polite";
  const t10 = `${index + 1} of ${count}: ${slide.eyebrow}`;
  const t11 = reduceMotion ? fadeVariants : stageVariants;
  let t12;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      duration: SLIDE_TRANSITION_MS / 1000,
      ease: [0.22, 1, 0.36, 1]
    };
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== slide) {
    t13 = <SlideHeading slide={slide} data-sentry-element="SlideHeading" data-sentry-source-file="feature-deck.tsx" />;
    $[10] = slide;
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  let t14;
  if ($[12] !== reduceMotion) {
    t14 = reduceMotion ? false : {
      opacity: 0,
      scale: 0.94
    };
    $[12] = reduceMotion;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  let t15;
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      opacity: 1,
      scale: 1
    };
    t16 = {
      type: "spring",
      stiffness: 220,
      damping: 20,
      delay: 0.08
    };
    $[14] = t15;
    $[15] = t16;
  } else {
    t15 = $[14];
    t16 = $[15];
  }
  let t17;
  if ($[16] !== slide) {
    t17 = <SlideVisual slide={slide} data-sentry-element="SlideVisual" data-sentry-source-file="feature-deck.tsx" />;
    $[16] = slide;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== t14 || $[19] !== t17) {
    t18 = <div className="mt-10 flex min-h-[300px] w-full items-center justify-center"><motion.div className="flex w-full justify-center" initial={t14} animate={t15} transition={t16} data-sentry-element="motion.div" data-sentry-source-file="feature-deck.tsx">{t17}</motion.div></div>;
    $[18] = t14;
    $[19] = t17;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] !== reduceMotion) {
    t19 = reduceMotion ? false : {
      opacity: 0,
      y: 8
    };
    $[21] = reduceMotion;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  let t21;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      opacity: 1,
      y: 0
    };
    t21 = {
      duration: 0.3,
      delay: 0.3
    };
    $[23] = t20;
    $[24] = t21;
  } else {
    t20 = $[23];
    t21 = $[24];
  }
  let t22;
  if ($[25] !== slide.caption || $[26] !== t19) {
    t22 = <motion.p className="text-muted-foreground mt-10 max-w-[320px] text-center text-xs leading-normal md:text-sm" initial={t19} animate={t20} transition={t21} data-sentry-element="motion.p" data-sentry-source-file="feature-deck.tsx">{slide.caption}</motion.p>;
    $[25] = slide.caption;
    $[26] = t19;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  if ($[28] !== direction || $[29] !== slide.id || $[30] !== t10 || $[31] !== t11 || $[32] !== t13 || $[33] !== t18 || $[34] !== t22) {
    t23 = <motion.div key={slide.id} role="group" aria-roledescription="slide" aria-label={t10} className="flex w-full flex-col items-center" custom={direction} variants={t11} initial="enter" animate="center" exit="exit" transition={t12} data-sentry-element="motion.div" data-sentry-source-file="feature-deck.tsx">{t13}{t18}{t22}</motion.div>;
    $[28] = direction;
    $[29] = slide.id;
    $[30] = t10;
    $[31] = t11;
    $[32] = t13;
    $[33] = t18;
    $[34] = t22;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== direction || $[37] !== t23) {
    t24 = <AnimatePresence mode="wait" custom={direction} data-sentry-element="AnimatePresence" data-sentry-source-file="feature-deck.tsx">{t23}</AnimatePresence>;
    $[36] = direction;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t24 || $[40] !== t9) {
    t25 = <div aria-live={t9} className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-8 py-10 xl:px-16">{t24}</div>;
    $[39] = t24;
    $[40] = t9;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== autoplay || $[43] !== isUserPaused) {
    t26 = autoplay ? <Button variant="unstyled" size={null} aria-label={isUserPaused ? "Play slides" : "Pause slides"} className="text-muted-foreground hover:text-foreground size-6 items-center justify-center rounded-full" onClick={() => setIsUserPaused(_temp)}>{isUserPaused ? <Play /> : <Pause />}</Button> : null;
    $[42] = autoplay;
    $[43] = isUserPaused;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== advance || $[46] !== autoplay || $[47] !== deck.slides || $[48] !== index || $[49] !== paused || $[50] !== showSlide) {
    t27 = <SlideProgress slides={deck.slides} activeIndex={index} durationMs={SLIDE_DURATION_MS} paused={paused} autoplay={autoplay} onSelect={showSlide} onComplete={advance} data-sentry-element="SlideProgress" data-sentry-source-file="feature-deck.tsx" />;
    $[45] = advance;
    $[46] = autoplay;
    $[47] = deck.slides;
    $[48] = index;
    $[49] = paused;
    $[50] = showSlide;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t26 || $[53] !== t27) {
    t28 = <div className="flex w-full items-center justify-center gap-3 px-8 pb-8">{t26}{t27}</div>;
    $[52] = t26;
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== deck.label || $[56] !== t25 || $[57] !== t28) {
    t29 = <section aria-roledescription="carousel" aria-label={deck.label} className="flex min-h-0 w-full flex-1 flex-col items-center" onPointerEnter={t5} onPointerLeave={t6} onFocus={t7} onBlur={t8} data-sentry-component="FeatureDeck" data-sentry-source-file="feature-deck.tsx">{t25}{t28}</section>;
    $[55] = deck.label;
    $[56] = t25;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  return t29;
}
function _temp(isPaused) {
  return !isPaused;
}